import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const NotFound = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16 flex items-center justify-center">
      <div className="section-padding w-full">
        <div className="container-max">
          <motion.div
            className="text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* 404 Number */}
            <motion.div variants={itemVariants} className="mb-8">
              <h1 className="text-8xl md:text-9xl font-serif font-bold text-deep-brown opacity-10 -mb-6">
                404
              </h1>
              <p className="text-6xl md:text-7xl font-serif font-light text-deep-brown relative z-10">
                Page Not Found
              </p>
            </motion.div>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl text-warm-gray max-w-md mx-auto mb-8"
            >
              We couldn't find the page you're looking for. The link might be broken or the page may have been moved.
            </motion.p>

            {/* Illustration / Visual Element */}
            <motion.div
              variants={itemVariants}
              className="mb-12 flex justify-center"
            >
              <div className="relative w-48 h-48 md:w-64 md:h-64">
                <svg
                  className="w-full h-full text-warm-gray opacity-30"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.button
                onClick={() => navigate('/')}
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Go to Homepage
              </motion.button>

              <motion.button
                onClick={() => navigate(-1)}
                className="btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Go Back
              </motion.button>
            </motion.div>

            {/* Helpful Links */}
            <motion.div
              variants={itemVariants}
              className="mt-12 pt-8 border-t border-warm-gray/20"
            >
              <p className="text-sm text-warm-gray mb-4">
                Looking for something? Try these:
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/"
                  className="text-deep-brown hover:text-gold transition-colors duration-200 underline"
                >
                  Home
                </Link>
                <span className="text-warm-gray/30">•</span>
                <Link
                  to="/indian-wear"
                  className="text-deep-brown hover:text-gold transition-colors duration-200 underline"
                >
                  Indian Wear
                </Link>
                <span className="text-warm-gray/30">•</span>
                <Link
                  to="/western-wear"
                  className="text-deep-brown hover:text-gold transition-colors duration-200 underline"
                >
                  Western Wear
                </Link>
                <span className="text-warm-gray/30">•</span>
                <Link
                  to="/contact"
                  className="text-deep-brown hover:text-gold transition-colors duration-200 underline"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            {/* Error Code */}
            <motion.p
              variants={itemVariants}
              className="mt-8 text-xs text-warm-gray/50"
            >
              Error Code: 404 | Page Not Found
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
